import {Component, inject, OnInit} from '@angular/core'
import {PageEvent} from '@angular/material/paginator'
import {Store} from '@ngrx/store'
import {AutomationProcessListAPIModel, FileAPIModel, ListExpenseAPIRequest, StatusGroupings} from 'src/models/api.model'
import {CommonService} from 'src/services/common/common.service'
import {AppState} from 'src/store/state/app.state'
import {ColumnConfig, TableData} from '../common/table/table.component'
import {ActivatedRoute, Router} from '@angular/router'
import {Observable} from 'rxjs'
import {AutomationProcessRegisterService} from 'src/services/automation-register/automation-register.service'
import {FETCH_AUTOMATION_PROCESS_LIST} from 'src/store/action/automation-process-register.action'
import {TableService} from 'src/services/table.service'
import {FileRepositoryDialogComponent} from "../common/file-repository-dialog/file-repository-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {ExecutionSummaryDialogComponent} from "../common/execution-summary-dialog/execution-summary-dialog.component";
import {FilterProps, FilterType} from "../common/filter-panel/filter-panel.component";
import {ProcessingStatus, Status} from "../../constants/global-constants";

@Component({
  selector: 'app-execution-logs',
  templateUrl: './execution-logs.component.html',
  styleUrls: ['./execution-logs.component.scss']
})
export class ExecutionLogsComponent implements OnInit {
  data$: Observable<TableData<AutomationProcessListAPIModel>>
  readonly dialog = inject(MatDialog)

  requestPayload: ListExpenseAPIRequest = {
    filters: {},
    sort: {
      sortBy: 'createdDatetime',
      sortOrder: 'DESC'
    },
    pageInfo: { pageNo: 0, limit: 10 }
  }

  filters: { [key: string]: string[] | string } = {};
  filterProps: FilterProps[] = [
    {
      field: 'status',
      label: 'Status',
      type: FilterType.SELECT,
      options: Object.values(ProcessingStatus),
    },
    {
      field: 'createdDatetime',
      label: 'Processing Date',
      type: FilterType.DATE,
    },
    {
      field: 'runNumber',
      label: 'Run Number',
      type: FilterType.TEXT,
    },
  ]

  automationRegisterProcessColumns: ColumnConfig<AutomationProcessListAPIModel>[] = [
    {
      columnDef: 'createdDatetime',
      type: 'DATE',
      header: 'Date',
      cell: (element: AutomationProcessListAPIModel) => this.cs.getDateFromDateTime(element.createdDatetime),
      sortable: true,
      isHtml: true
    },
    {
      columnDef: 'time',
      type: 'DATE',
      header: 'Time',
      cell: (element: AutomationProcessListAPIModel) => this.cs.getTimeFromDateTime(element.createdDatetime),
      sortable: true,
      isHtml: true
    },
    {
      columnDef: 'status',
      type: 'TEXT',
      header: 'Status',
      cell: (element: AutomationProcessListAPIModel) => this.tableService.buildProcessingStatusChip(element.status),
      isHtml: true
    },
    {
      columnDef: 'triggerBy',
      type: 'TEXT',
      header: 'Trigger By',
      cell: (element: AutomationProcessListAPIModel) => `${element.triggerBy || ''}`,
    },
    {
      columnDef: 'statuses',
      type: 'TEXT',
      header: 'Expense Process',
      cell: (element: AutomationProcessListAPIModel) => {
        let html = '';
        Object.entries(element?.processedExpenseStatusCountsMap ? element?.processedExpenseStatusCountsMap : []).forEach(([status, count]) => {
          html += `${this.tableService.buildStatusChipForExpReport(status, count)}<br />`;
        });
        return html;
      },
      isHtml: true
    },
    {
      columnDef: 'overview',
      type: 'TEXT',
      header: 'Processing Summary',
      cell: (element: AutomationProcessListAPIModel) => {
        return `<a class="html-anchor-cell" data-action="process-overview">Summary</a>`
      },
      isHtml: true,
    },
    {
      columnDef: 'files',
      type: 'TEXT',
      header: 'File Repository',
      cell: (element: AutomationProcessListAPIModel) => {
          return `<a class="html-anchor-cell" data-action="file-repository">View</a>`
      },
      sortable: false,
      filterable: false,
      isHtml: true,
    },
  ]

  constructor(
    private route: ActivatedRoute,
    private cs: CommonService,
    private appState: Store<AppState>,
    private router: Router,
    private automationService: AutomationProcessRegisterService,
    private tableService: TableService
  ) {}

  ngOnInit() {
    const runNumber = this.route.snapshot.queryParamMap.get('runNumber');
    if (runNumber) {
      this.filters['runNumber'] = runNumber;
    }
    this.fetchAutomationProcessRegisterList()
    this.data$ = this.automationService.getAutomationProcessRegisterListFromStore();
  }

  private fetchAutomationProcessRegisterList() {
    this.requestPayload = {
      ...this.requestPayload,
      filters: this.filters
    }
    this.appState.dispatch(FETCH_AUTOMATION_PROCESS_LIST({ payload: this.requestPayload }))
  }

  onPageChange(event: PageEvent) {
    this.requestPayload = {
      ...this.requestPayload,
      pageInfo: {
        pageNo: event.pageIndex,
        limit: event.pageSize
      }
    }
    this.fetchAutomationProcessRegisterList()
  }

  onSortChange(event: { active: string; direction: string }) {
    if (!event.active || !event.direction) {
      return
    }
    this.requestPayload = {
      ...this.requestPayload,
      sort: {
        sortBy: event.active,
        sortOrder: event.direction
      }
    }
    this.fetchAutomationProcessRegisterList()
  }

  onFilterChange(filters: { [key: string]: string[] | string }) {
    this.filters = filters;
    this.fetchAutomationProcessRegisterList()
  }

  handleCellClick(event: { column: string; element: any; action: string; context?: string }) {
    const action = event.action
    switch (action) {
      case "view-expenses": {
        let paymentStatus = event.context;
        const processId = event.element.id
        this.router.navigate(['/expenses'], {queryParams: {processId, paymentStatus}});
        break
      }
      case "view-expenses-for-currency-groups": {
        let currency = event.context;
        const processId = event.element.id
        this.router.navigate(['/expenses'], {queryParams: {processId, currency}});
        break
      }
      case "view-expenses-for-entity-groups": {
        let entity = event.context;
        const processId = event.element.id
        this.router.navigate(['/expenses'], {queryParams: {processId, entity}});
        break
      }
      case "file-repository": {
        this.openFileRepositoryDialog(event.element.files)
        break
      }
      case "process-overview": {
        this.openProcessOverviewDialog(event.element.statusGroupings)
        break
      }
    }
  }

  openProcessOverviewDialog(statusGroupings: StatusGroupings): void {
    this.dialog.open(ExecutionSummaryDialogComponent, {
      width: '500px',
      maxHeight: '800px;',
      data: statusGroupings
    })
  }

  openFileRepositoryDialog(files: FileAPIModel[]): void {
    // Sort files by type (assuming 'type' property exists on FileAPIModel)
    const sortedFiles = [...files].sort((a, b) => {
      if (a.type && b.type) {
        return a.type.localeCompare(b.type);
      }
      // fallback if type is missing
      return 0;
    });
    this.dialog.open(FileRepositoryDialogComponent, {
      width: '1200px',
      data: sortedFiles
    })
  }
}
